/**
 * File: src/store/slices/guest-evidence.js
 * Layer: State Management
 * Description: Stores the authenticated guest evidence request and linked
 * assessment context for guest portal pages.
 */
import { createSlice } from '@reduxjs/toolkit';

import {
    getMyGuestEvidence as getMyGuestEvidenceAPI,
    updateGuestInformation as updateGuestInformationAPI,
    getCandidateGuestRequests as getCandidateGuestRequestsAPI,
    getCandidateGuestOptions as getCandidateGuestOptionsAPI,
    getAssessorGuestOptions as getAssessorGuestOptionsAPI,
    inviteGuest,
    declineGuestEvidenceRequest,
    completeGuestEvidenceUpload as completeGuestEvidenceUploadAPI,
    createGuestEvidenceUploadUrl as createGuestEvidenceUploadUrlAPI,
    getGuestEvidenceAccessUrl as getGuestEvidenceAccessUrlAPI,
    getGuestEvidenceLibrary as getGuestEvidenceLibraryAPI,
    linkGuestEvidence as linkGuestEvidenceAPI,
    removeCandidateGuestRequest as removeCandidateGuestRequestAPI,
    submitGuestEvidenceRequest as submitGuestEvidenceRequestAPI,
    uploadGuestEvidenceToS3 as uploadGuestEvidenceToS3API,
    updateGuestEvidenceMetadata as updateGuestEvidenceMetadataAPI,
    assessmentGuestList
} from 'api/guest-evidence';

const initialCandidateRequestsPagination = {
    total: 0,
    totalItems: 0
};

/**
 * Currently Use for Assessor Login Guest List Tab in
 * Outcome & Evidence Review
 */
const defaultParams = {
    filter: {
        search: ''
    },
    pageNo: 1,
    perPage: 5,
    sort: {
        sortBy: 'createdAt',
        sortDirection: 'DESC'
    }
};

const initialState = {
    detail: null,
    candidateRequests: [],
    candidateRequestsPagination: initialCandidateRequestsPagination,
    guestOptions: [],
    guestOptionsLoading: false,
    loading: false,
    candidateRequestsLoading: false,
    evidenceLibrary: [],
    evidenceLibraryLoading: false,
    error: '',
    candidateRequestsError: '',
    guestOptionsError: '',
    evidenceLibraryError: '',
    submitting: false,
    // For RTO Assessor Log in Guest List
    isFetching: true,
    guests: [],
    params: defaultParams
};

const slice = createSlice({
    name: 'guestEvidence',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload || '';
        },
        setCandidateRequestsLoading(state, action) {
            state.candidateRequestsLoading = action.payload;
        },
        setCandidateRequestsError(state, action) {
            state.candidateRequestsError = action.payload || '';
        },
        setGuestOptionsLoading(state, action) {
            state.guestOptionsLoading = action.payload;
        },
        setGuestOptionsError(state, action) {
            state.guestOptionsError = action.payload || '';
        },
        setEvidenceLibraryLoading(state, action) {
            state.evidenceLibraryLoading = action.payload;
        },
        setEvidenceLibraryError(state, action) {
            state.evidenceLibraryError = action.payload || '';
        },
        setDetail(state, action) {
            state.detail = action.payload?.data || null;
        },
        setRequest(state, action) {
            state.detail = {
                ...(state.detail || {}),
                request: action.payload?.data || null
            };
        },
        setCandidateRequests(state, action) {
            state.candidateRequests = action.payload?.data || [];
            state.candidateRequestsPagination = action.payload?.pagination || initialCandidateRequestsPagination;
        },
        setGuestOptions(state, action) {
            state.guestOptions = action.payload?.data || [];
        },
        setEvidenceLibrary(state, action) {
            state.evidenceLibrary = action.payload?.data?.files || [];
        },
        clearGuestEvidence(state) {
            state.detail = null;
            state.candidateRequests = [];
            state.candidateRequestsPagination = initialCandidateRequestsPagination;
            state.guestOptions = [];
            state.evidenceLibrary = [];
            state.error = '';
            state.candidateRequestsError = '';
            state.guestOptionsError = '';
            state.evidenceLibraryError = '';
        },
        setSubmitting(state, action) {
            state.submitting = action.payload;
        },
        setIsFetchingGuests(state, action) {
            state.isFetching = action.payload;
        },
        setParams(state, action) {
            const { params, isResetParams = false } = action.payload;
            state.params = isResetParams ? defaultParams : params;
        },
        setGuests(state, action) {
            state.guests = action.payload.guests;
            state.count = action.payload.count;
        }
    }
});

export const { setParams, setIsFetchingGuests } = slice.actions;

export default slice.reducer;

export function fetchGuestEvidence() {
    return async (dispatch) => {
        dispatch(slice.actions.setLoading(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await getMyGuestEvidenceAPI();
            dispatch(slice.actions.setDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to load guest evidence.'));
            throw err;
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

/**
 * Save guest information and refresh the invitation used throughout the portal.
 * @param {Object} data - Guest details and draft mode.
 * @returns {Function} Async Redux action.
 */
export function saveGuestInformation(data) {
    return async (dispatch) => {
        const response = await updateGuestInformationAPI(data);
        if (!response?.status || !response?.data?._id) throw new Error('Unable to save guest information.');
        dispatch(slice.actions.setRequest(response));
        return response;
    };
}

/**
 * Fetch Guest List in RTO Login
 */
export const fetchGuestList = (asessmentCode, { unitCode, outcomeId, pageNo, perPage, filter, sort, guestId = null } = {}) => {
    return async (dispatch) => {
        try {
            const data = {
                unitCode,
                outcomeId,
                pageNo,
                pageSize: perPage,
                search: filter?.search || '',
                sortBy: sort?.sortBy || 'name',
                sortDirection: sort?.sortDirection || 'ASC',
                guestId
            };

            const result = await assessmentGuestList(asessmentCode, data);

            const { list = [], total = 0 } = result.data;

            dispatch(
                slice.actions.setGuests({
                    guests: list,
                    count: total
                })
            );
        } catch (err) {
            console.error('Error fetching guests:', err);
            dispatch(
                slice.actions.setGuests({
                    guests: [],
                    count: 0
                })
            );
        }
    };
};

/**
 * Load guest request records for one candidate assessment.
 * @param {string} assessmentCode - Candidate assessment code
 * @param {Object} params - Guest request list filters and pagination
 * @returns {Function}
 */
export function fetchCandidateGuestRequests(assessmentCode, params = {}) {
    return async (dispatch) => {
        dispatch(slice.actions.setCandidateRequestsLoading(true));
        dispatch(slice.actions.setCandidateRequestsError(''));

        try {
            const response = await getCandidateGuestRequestsAPI(assessmentCode, params);
            dispatch(slice.actions.setCandidateRequests(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setCandidateRequestsError(err?.message || 'Unable to load guest requests.'));
            throw err;
        } finally {
            dispatch(slice.actions.setCandidateRequestsLoading(false));
        }
    };
}

export function fetchCandidateGuestOptions(assessmentCode, params = {}) {
    return async (dispatch) => {
        dispatch(slice.actions.setGuestOptionsLoading(true));
        dispatch(slice.actions.setGuestOptionsError(''));

        try {
            const response = await getCandidateGuestOptionsAPI(assessmentCode, params);
            dispatch(slice.actions.setGuestOptions(response));
            return response.data || [];
        } catch (err) {
            dispatch(slice.actions.setGuestOptionsError(err?.message || 'Unable to load guest options.'));
            throw err;
        } finally {
            dispatch(slice.actions.setGuestOptionsLoading(false));
        }
    };
}

export function fetchAssessorGuestOptions(assessmentCode, params = {}) {
    return async (dispatch) => {
        dispatch(slice.actions.setGuestOptionsLoading(true));
        dispatch(slice.actions.setGuestOptionsError(''));

        try {
            const response = await getAssessorGuestOptionsAPI(assessmentCode, params);
            dispatch(slice.actions.setGuestOptions(response));
            return response.data || [];
        } catch (err) {
            dispatch(slice.actions.setGuestOptionsError(err?.message || 'Unable to load assessor guest options.'));
            throw err;
        } finally {
            dispatch(slice.actions.setGuestOptionsLoading(false));
        }
    };
}

/**
 * Submit a guest invitation while keeping dialog submit state in sync.
 * @param {Object} data - Guest invitation payload
 * @returns {Function}
 */
export function createGuestInvite(data) {
    return async (dispatch) => {
        dispatch(slice.actions.setSubmitting(true));

        try {
            return await inviteGuest(data);
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function removeCandidateGuestRequest(assessmentCode, guestRequestId, params = {}) {
    return async (dispatch) => {
        if (!assessmentCode || !guestRequestId) return null;

        dispatch(slice.actions.setSubmitting(true));

        try {
            const response = await removeCandidateGuestRequestAPI(assessmentCode, guestRequestId);
            await dispatch(fetchCandidateGuestRequests(assessmentCode, params));
            return response;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

/**
 * Submit a guest invitation while keeping dialog submit state in sync.
 * @param {Object} data - Guest invitation payload
 * @returns {Function}
 */
export function declineGuestRequest(data = {}) {
    return async () => declineGuestEvidenceRequest(data);
}

export function fetchGuestEvidenceLibrary() {
    return async (dispatch) => {
        dispatch(slice.actions.setEvidenceLibraryLoading(true));
        dispatch(slice.actions.setEvidenceLibraryError(''));

        try {
            const response = await getGuestEvidenceLibraryAPI();
            dispatch(slice.actions.setEvidenceLibrary(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setEvidenceLibraryError(err?.message || 'Unable to load existing evidence.'));
            throw err;
        } finally {
            dispatch(slice.actions.setEvidenceLibraryLoading(false));
        }
    };
}

export function createGuestEvidenceUploadUrl(data) {
    return async () => createGuestEvidenceUploadUrlAPI(data);
}

export function uploadGuestEvidenceToS3(data) {
    return async () => uploadGuestEvidenceToS3API(data);
}

export function completeGuestEvidenceUpload(evidenceId) {
    return async () => completeGuestEvidenceUploadAPI(evidenceId);
}

export function updateGuestEvidenceMetadata(evidenceId, data) {
    return async () => updateGuestEvidenceMetadataAPI(evidenceId, data);
}

export function getGuestEvidenceAccessUrl(evidenceId, mode = 'preview') {
    return async () => getGuestEvidenceAccessUrlAPI(evidenceId, mode);
}

export function linkGuestEvidence(data) {
    return async (dispatch) => {
        dispatch(slice.actions.setSubmitting(true));

        try {
            const response = await linkGuestEvidenceAPI(data);
            dispatch(slice.actions.setDetail(response));
            return response;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function submitGuestEvidenceRequest() {
    return async (dispatch) => {
        dispatch(slice.actions.setSubmitting(true));

        try {
            const response = await submitGuestEvidenceRequestAPI();
            dispatch(slice.actions.setRequest(response));
            return response;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export const { clearGuestEvidence } = slice.actions;
